import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';

/**
 * Per-day-group context that powers the "Move Foods" mode.
 *
 * When active, all Meal activities inside the provider are forced into
 * a draggable mode and share a single working state. Foods can be moved
 * within the same meal OR between meals on the same day. Each successful
 * drop persists the affected meals via `backendService.moveFoods(...)`.
 */
const MoveFoodsContext = createContext(null);

const buildDisplayItems = (foods) => {
  const items = [];
  const processed = new Set();
  (foods || []).forEach((food, idx) => {
    if (food.recipe_group_id) {
      if (!processed.has(food.recipe_group_id)) {
        processed.add(food.recipe_group_id);
        const groupFoods = foods.filter(f => f.recipe_group_id === food.recipe_group_id);
        items.push({
          type: 'recipe_group',
          recipe_group_id: food.recipe_group_id,
          recipe_name: food.recipe_name,
          recipe_scale: food.recipe_scale,
          foods: groupFoods,
          startIndex: idx,
        });
      }
    } else {
      items.push({ type: 'food', food, index: idx });
    }
  });
  return items;
};

const flattenDisplayItems = (items) =>
  items.flatMap(it => (it.type === 'recipe_group' ? it.foods : [it.food]));

export const MoveFoodsProvider = ({ children, backendService, onActiveChange }) => {
  const [isActive, setIsActive] = useState(false);
  const [activeOwnerMealId, setActiveOwnerMealId] = useState(null);
  const [recipeOwnerMealId, setRecipeOwnerMealId] = useState(null);
  const selectionActive = isActive || recipeOwnerMealId !== null;
  const onActiveChangeRef = useRef(onActiveChange);
  useEffect(() => { onActiveChangeRef.current = onActiveChange; }, [onActiveChange]);
  useEffect(() => {
    onActiveChangeRef.current?.(selectionActive);
    return () => onActiveChangeRef.current?.(false);
  }, [selectionActive]);
  const pointerYRef = useRef(null);
  const autoScrollFrameRef = useRef(null);

  useEffect(() => {
    if (!isActive) return undefined;

    const EDGE_ZONE = 96;
    const MAX_SCROLL_SPEED = 18;
    const updatePointer = (event) => {
      const point = event.touches?.[0] || event;
      if (typeof point.clientY === 'number') pointerYRef.current = point.clientY;
    };
    const clearPointer = () => {
      pointerYRef.current = null;
    };
    const autoScroll = () => {
      const pointerY = pointerYRef.current;
      if (pointerY !== null) {
        const viewportHeight = window.innerHeight;
        let scrollDelta = 0;

        if (pointerY < EDGE_ZONE) {
          scrollDelta = -Math.ceil(((EDGE_ZONE - pointerY) / EDGE_ZONE) * MAX_SCROLL_SPEED);
        } else if (pointerY > viewportHeight - EDGE_ZONE) {
          scrollDelta = Math.ceil(((pointerY - (viewportHeight - EDGE_ZONE)) / EDGE_ZONE) * MAX_SCROLL_SPEED);
        }

        if (scrollDelta) window.scrollBy(0, scrollDelta);
      }

      autoScrollFrameRef.current = requestAnimationFrame(autoScroll);
    };

    window.addEventListener('dragover', updatePointer, { passive: true });
    window.addEventListener('touchmove', updatePointer, { passive: true });
    window.addEventListener('drop', clearPointer, true);
    window.addEventListener('dragend', clearPointer, true);
    window.addEventListener('touchend', clearPointer, { passive: true });
    window.addEventListener('touchcancel', clearPointer, { passive: true });
    autoScrollFrameRef.current = requestAnimationFrame(autoScroll);

    return () => {
      window.removeEventListener('dragover', updatePointer);
      window.removeEventListener('touchmove', updatePointer);
      window.removeEventListener('drop', clearPointer, true);
      window.removeEventListener('dragend', clearPointer, true);
      window.removeEventListener('touchend', clearPointer);
      window.removeEventListener('touchcancel', clearPointer);
      if (autoScrollFrameRef.current) cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
      pointerYRef.current = null;
    };
  }, [isActive]);

  // mealsState: { [mealId]: foods[] } — local working copy during move mode
  const [mealsState, setMealsState] = useState({});
  // Latest mealsState in a ref so persist callbacks don't capture stale data
  const mealsStateRef = useRef({});
  useEffect(() => { mealsStateRef.current = mealsState; }, [mealsState]);

  // Registered meals for this day group (self-registered by Meal components)
  const registeredMealsRef = useRef(new Map()); // mealId -> meal object

  // Where a cross-meal drag would land: { mealId, index, height }. Drives the
  // placeholder gap so a cross-meal drop previews like a same-meal reorder.
  const [dropPreview, setDropPreviewState] = useState(null);
  const dropPreviewRef = useRef(null);

  const setDropPreview = useCallback((mealId, index, height) => {
    const next = { mealId: String(mealId), index, height: height || 52 };
    const prev = dropPreviewRef.current;
    if (prev && prev.mealId === next.mealId && prev.index === next.index && prev.height === next.height) {
      return;
    }
    dropPreviewRef.current = next;
    setDropPreviewState(next);
  }, []);

  const clearDropPreview = useCallback(() => {
    if (!dropPreviewRef.current) return;
    dropPreviewRef.current = null;
    setDropPreviewState(null);
  }, []);

  // Read through the ref so hover handlers can skip redundant work without
  // waiting for a render.
  const isDropPreviewAt = useCallback((mealId, index) => {
    const prev = dropPreviewRef.current;
    return !!prev && prev.mealId === String(mealId) && prev.index === index;
  }, []);

  const registerMeal = useCallback((meal) => {
    if (!meal || !meal._id) return;
    registeredMealsRef.current.set(meal._id.toString(), meal);
  }, []);

  const unregisterMeal = useCallback((mealId) => {
    registeredMealsRef.current.delete(mealId.toString());
    setRecipeOwnerMealId(current => current === String(mealId) ? null : current);
  }, []);

  const enter = useCallback((ownerMealId) => {
    if ([...registeredMealsRef.current.values()].some(meal => meal._mealMutationPending)) {
      return false;
    }
    setRecipeOwnerMealId(null);
    const initial = {};
    registeredMealsRef.current.forEach((meal, mealId) => {
      initial[mealId] = [...((meal.data && meal.data.foods) || [])];
    });
    mealsStateRef.current = initial;
    setMealsState(initial);
    setActiveOwnerMealId(ownerMealId ? String(ownerMealId) : null);
    setIsActive(true);
    return true;
  }, []);

  const exit = useCallback(() => {
    setIsActive(false);
    setActiveOwnerMealId(null);
    mealsStateRef.current = {};
    setMealsState({});
    dropPreviewRef.current = null;
    setDropPreviewState(null);
  }, []);

  const getFoodsFor = useCallback((mealId) => {
    if (!mealId) return null;
    return mealsState[mealId.toString()] || null;
  }, [mealsState]);

  const enterRecipe = useCallback((ownerMealId) => {
    exit();
    setRecipeOwnerMealId(String(ownerMealId));
  }, [exit]);

  const exitRecipe = useCallback((ownerMealId) => {
    setRecipeOwnerMealId(current => current === String(ownerMealId) ? null : current);
  }, []);

  // Returns the set of meal ids currently in move mode
  const activeMealIds = Object.keys(mealsState);

  /**
   * Move a display item (food or recipe group) from one meal+index to
   * another. Same-meal and cross-meal moves are handled identically at
   * the display-item level so recipe groups always travel atomically.
   */
  const moveItem = useCallback((fromMealId, fromIdx, toMealId, toIdx) => {
    const fromKey = String(fromMealId);
    const toKey = String(toMealId);
    setMealsState(prev => {
      const fromFoods = prev[fromKey];
      const toFoods = prev[toKey];
      if (!fromFoods || !toFoods) return prev;

      let next;
      if (fromKey === toKey) {
        const items = buildDisplayItems(fromFoods);
        if (fromIdx < 0 || fromIdx >= items.length) return prev;
        const clamped = Math.max(0, Math.min(toIdx, items.length - 1));
        if (clamped === fromIdx) return prev;
        const [moved] = items.splice(fromIdx, 1);
        items.splice(clamped, 0, moved);
        next = { ...prev, [fromKey]: flattenDisplayItems(items) };
      } else {
        const fromItems = buildDisplayItems(fromFoods);
        const toItems = buildDisplayItems(toFoods);
        if (fromIdx < 0 || fromIdx >= fromItems.length) return prev;
        const [moved] = fromItems.splice(fromIdx, 1);
        const insertAt = Math.max(0, Math.min(toIdx, toItems.length));
        toItems.splice(insertAt, 0, moved);
        next = {
          ...prev,
          [fromKey]: flattenDisplayItems(fromItems),
          [toKey]: flattenDisplayItems(toItems),
        };
      }
      // Keep the ref in sync synchronously so that persistMeals (called
      // from react-dnd's `end` callback right after the last hover) always
      // sees the latest working state, even before React flushes the
      // commit-time effect that mirrors `mealsState` into the ref.
      mealsStateRef.current = next;
      return next;
    });
  }, []);

  /**
   * Persist the given meals' current state to the backend in a single
   * call. Called from drag-end with the source + destination meal ids.
   */
  const persistMeals = useCallback(async (mealIds) => {
    if (!backendService || !backendService.moveFoods) return;
    const unique = Array.from(new Set(mealIds.map(String)));
    const moves = unique
      .filter(mid => Array.isArray(mealsStateRef.current[mid]))
      .map(mid => ({ mealId: mid, foods: mealsStateRef.current[mid] }));
    if (moves.length === 0) return;
    try {
      await backendService.moveFoods(moves);
    } catch (err) {
      console.error('Failed to persist move-foods:', err);
    }
  }, [backendService]);

  const value = {
    isActive,
    recipeOwnerMealId,
    enterRecipe,
    exitRecipe,
    activeOwnerMealId,
    enter,
    exit,
    moveItem,
    persistMeals,
    getFoodsFor,
    registerMeal,
    unregisterMeal,
    activeMealIds,
    dropPreview,
    setDropPreview,
    clearDropPreview,
    isDropPreviewAt,
    buildDisplayItems,
  };

  return (
    <MoveFoodsContext.Provider value={value}>
      {children}
    </MoveFoodsContext.Provider>
  );
};

export const useMoveFoods = () => useContext(MoveFoodsContext);

// Safe variant for components that may render outside a provider — returns
// a no-op stub so callers don't need to null-check everywhere.
export const useMoveFoodsOptional = () => {
  const ctx = useContext(MoveFoodsContext);
  if (ctx) return ctx;
  return {
    isActive: false,
    // Undefined identifies a standalone Meal, where selection is locally owned.
    recipeOwnerMealId: undefined,
    enterRecipe: () => {},
    exitRecipe: () => {},
    activeOwnerMealId: null,
    enter: () => {},
    exit: () => {},
    moveItem: () => {},
    persistMeals: async () => {},
    getFoodsFor: () => null,
    registerMeal: () => {},
    unregisterMeal: () => {},
    activeMealIds: [],
    dropPreview: null,
    setDropPreview: () => {},
    clearDropPreview: () => {},
    isDropPreviewAt: () => false,
    buildDisplayItems,
  };
};
